export const DASHBOARD_RANGE_LAST_7_DAYS = "LAST_7_DAYS" as const;
export const DASHBOARD_RANGE_LAST_15_DAYS = "LAST_15_DAYS" as const;
export const DASHBOARD_RANGE_LAST_30_DAYS = "LAST_30_DAYS" as const;

export const DASHBOARD_RANGES = [
  DASHBOARD_RANGE_LAST_7_DAYS,
  DASHBOARD_RANGE_LAST_15_DAYS,
  DASHBOARD_RANGE_LAST_30_DAYS,
] as const;

export type DashboardRange = (typeof DASHBOARD_RANGES)[number];

export const DASHBOARD_FRESHNESS_FRESH = "FRESH" as const;
export const DASHBOARD_FRESHNESS_DELAYED = "DELAYED" as const;
export const DASHBOARD_FRESHNESS_STALE = "STALE" as const;
export const DASHBOARD_FRESHNESS_UNAVAILABLE = "UNAVAILABLE" as const;

export const DASHBOARD_FRESHNESS_STATUSES = [
  DASHBOARD_FRESHNESS_FRESH,
  DASHBOARD_FRESHNESS_DELAYED,
  DASHBOARD_FRESHNESS_STALE,
  DASHBOARD_FRESHNESS_UNAVAILABLE,
] as const;

export type DashboardFreshnessStatus = (typeof DASHBOARD_FRESHNESS_STATUSES)[number];

export const DASHBOARD_KPI_VALUE_COUNT = "COUNT" as const;
export const DASHBOARD_KPI_VALUE_MONEY_MINOR = "MONEY_MINOR" as const;
export const DASHBOARD_KPI_VALUE_PERCENT = "PERCENT" as const;

export const DASHBOARD_KPI_VALUE_KINDS = [
  DASHBOARD_KPI_VALUE_COUNT,
  DASHBOARD_KPI_VALUE_MONEY_MINOR,
  DASHBOARD_KPI_VALUE_PERCENT,
] as const;

export type DashboardKpiValueKind = (typeof DASHBOARD_KPI_VALUE_KINDS)[number];
export type DashboardTone = "blue" | "green" | "orange" | "red" | "purple" | "gray";
export type DashboardTrendDirection = "UP" | "DOWN" | "FLAT";
export type DashboardDueTone = "red" | "orange" | "gray";

export interface MerchantDashboardDto {
  readonly filters: DashboardFiltersDto;
  readonly freshness: DashboardFreshnessDto;
  readonly kpiCards: readonly DashboardKpiCardDto[];
  readonly funnel: DashboardFunnelDto;
  readonly outcomeMix: DashboardOutcomeMixDto;
  readonly needsAttention: DashboardNeedsAttentionDto;
  readonly challengedProducts: readonly DashboardRankedProductDto[];
  readonly competitorPressure: readonly DashboardCompetitorPressureDto[];
}

export interface DashboardFiltersDto {
  readonly range: DashboardRange;
  readonly fromDay: string;
  readonly toDay: string;
}

export interface DashboardFreshnessDto {
  readonly status: DashboardFreshnessStatus;
  readonly generatedAt: string;
  readonly label: string;
  readonly latestDataAt?: string;
}

export interface DashboardKpiCardDto {
  readonly id: string;
  readonly label: string;
  readonly value: number | null;
  readonly valueKind: DashboardKpiValueKind;
  readonly detail: string;
  readonly tone: DashboardTone;
  readonly href: string;
  readonly currency?: string;
  readonly alert?: string;
  readonly trend?: DashboardKpiTrendDto;
}

export interface DashboardKpiTrendDto {
  readonly direction: DashboardTrendDirection;
  readonly value: number;
  readonly label: string;
}

export interface DashboardFunnelDto {
  readonly totalRequests: number;
  readonly steps: readonly DashboardFunnelStepDto[];
}

export interface DashboardFunnelStepDto {
  readonly id: string;
  readonly label: string;
  readonly value: number | null;
  readonly percentage: number | null;
  readonly href: string;
}

export interface DashboardOutcomeMixDto {
  readonly totalRequests: number;
  readonly outcomes: readonly DashboardOutcomeDto[];
}

export interface DashboardOutcomeDto {
  readonly id: string;
  readonly label: string;
  readonly value: number;
  readonly percentage: number;
  readonly tone: DashboardTone;
  readonly href: string;
}

export interface DashboardNeedsAttentionDto {
  readonly total: number;
  readonly overdue: number;
  readonly dueSoon: number;
  readonly rows: readonly DashboardNeedsAttentionRowDto[];
}

export interface DashboardNeedsAttentionRowDto {
  readonly requestId: string;
  readonly requestCode: string;
  readonly issue: string;
  readonly issueTone: DashboardDueTone | "blue" | "yellow";
  readonly productTitle: string;
  readonly productImageUrl?: string;
  readonly variantTitle?: string;
  readonly competitorName?: string;
  readonly reason: string;
  readonly dueLabel?: string;
  readonly dueTone?: DashboardDueTone;
  readonly valueMinor?: number;
  readonly currency?: string;
  readonly assigneeName?: string;
  readonly actionLabel: string;
  readonly href: string;
}

export interface DashboardRankedProductDto {
  readonly productId: string;
  readonly title: string;
  readonly imageUrl?: string;
  readonly variantTitle?: string;
  readonly requests: number;
  readonly offers: number;
  readonly orders: number;
  readonly revenueMinor: number | null;
  readonly currency?: string;
  readonly href: string;
}

export interface DashboardCompetitorPressureDto {
  readonly competitorId?: string;
  readonly competitorName: string;
  readonly domain: string;
  readonly logoUrl?: string;
  readonly requests: number;
  readonly averagePriceGapPercent?: number;
  readonly ordersConverted: number;
  readonly href: string;
}

export function isDashboardRange(value: string): value is DashboardRange {
  return DASHBOARD_RANGES.includes(value as DashboardRange);
}
